import React from "react";
import { ControllerRenderProps, FieldError } from "react-hook-form";
import { MultipleChoiceOptions, ValidationConstraints } from "@/components/tasks/types";
import ValidationEditor from "./shared/ValidationEditor";
import ChoiceEditor from "./shared/ChoiceEditor";

interface MultipleChoiceProps {
  options: MultipleChoiceOptions;
  field: Omit<ControllerRenderProps, "value" | "onChange"> & {
    value: any;
    onChange: (value: any) => void;
  };
  error?: FieldError;
  readOnly?: boolean;
}

export default function MultipleChoice({
  options,
  field,
  error,
  readOnly,
}: MultipleChoiceProps) {
  const { value, onChange, ...restField } = field;

  return (
    <div className="space-y-2">
      <div className="space-y-2">
        {options.choices.map((choice, index) => {
          const optionValue = choice.value || choice.name;

          return (
            <label
              key={index}
              className="flex items-center space-x-2 cursor-pointer"
            >
              <input
                type={options.allowMultipleSelections ? "checkbox" : "radio"}
                {...restField}
                value={optionValue}
                className="text-blue-600 focus:ring-blue-500"
                disabled={readOnly}
                checked={
                  options.allowMultipleSelections
                    ? Array.isArray(value) && value.includes(optionValue)
                    : value === optionValue
                }
                onChange={(e) => {
                  if (options.allowMultipleSelections) {
                    const current = Array.isArray(value) ? value : [];
                    const next = e.target.checked
                      ? [...current, optionValue]
                      : current.filter((v) => v !== optionValue);
                    onChange(next);
                  } else {
                    onChange(optionValue);
                  }
                }}
              />
              <span className="text-gray-700">{choice.name}</span>
            </label>
          );
        })}
      </div>
      {error && <p className="text-sm text-red-600 mt-1">{error.message}</p>}
    </div>
  );
}

interface MultipleChoiceEditorProps {
  options: MultipleChoiceOptions;
  validation: ValidationConstraints;
  onOptionsChange: (options: MultipleChoiceOptions) => void;
  onValidationChange: (validation: ValidationConstraints) => void;
}

MultipleChoice.Editor = function MultipleChoiceEditor({
  options,
  validation,
  onOptionsChange,
  onValidationChange,
}: MultipleChoiceEditorProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="allow-multiple-selections"
          checked={options.allowMultipleSelections || false}
          onChange={(e) => {
            onOptionsChange({
              ...options,
              allowMultipleSelections: e.target.checked,
            });
          }}
          className="text-blue-600 focus:ring-blue-500 rounded"
        />
        <label
          htmlFor="allow-multiple-selections"
          className="text-sm font-medium text-gray-700"
        >
          Allow Multiple Selections
        </label>
      </div>

      <ChoiceEditor
        choices={options.choices}
        onChoicesChange={(choices) => onOptionsChange({ ...options, choices })}
      />

      <ValidationEditor
        validation={validation}
        promptType="multiple-choice"
        onValidationChange={onValidationChange}
      />
    </div>
  );
};
